/**
 * Godot's POINT_SIZE and POINT_COORD in three's programs: `use_point_size` sets each point's
 * square to `point_size` pixels (`material.cpp:1229-1232`, else 1 px, `scene_forward_clustered.glsl:446`)
 * and reads the albedo map at the point coordinate, not the UV (`material.cpp:1612-1615`).
 */
import { FrontSide } from 'three';
import { composeInjections } from '../materialProgramInputs';
import { editedProgram } from '../shaderPatch/chunkPatch';
import { recordedUniform } from './recordedOnMaterial';
import { BASE_MATERIAL_3D_DEFAULTS } from '../../godot/scene/baseMaterial3D';
import { bagForms } from './bagForms';
import { withDepthInjection } from './depthInjections';
import { INSTANCE_MATRIX_ATTRIBUTE } from '../meshes/sharedInstances';
const POINT_SIZE_KEY = 'godotPointSize';
const POINT_PIXEL_RATIO_KEY = 'godotPointPixelRatio';
const POINT_COORD_ALBEDO_KEY = 'godotPointCoordAlbedo';
const POINTS_AT_INSTANCES_KEY = 'godotPointsAtInstances';
const PROJECT_CHUNK = '#include <project_vertex>';
/** `scalars` as a points draw on a canvas of `pixelRatio`, or Godot's default surface for none. */
export function pointDrawOf(scalars, pixelRatio, placement) {
    const pointSize = scalars?.usesPointSize ? scalars.pointSize : undefined;
    return {
        pixels: pointSize ?? BASE_MATERIAL_3D_DEFAULTS.pointSize,
        pixelRatio,
        samplesAlbedoAtPointCoord: pointSize !== undefined,
        placement,
    };
}
/**
 * three defines `USE_INSTANCING` and declares `instanceMatrix` only for an InstancedMesh
 * (`WebGLPrograms.js:209`), so instanced points declare both. Each instance branch of three's
 * chunks and of the Godot patches then places, turns and billboards the points.
 */
const GODOT_INSTANCED_POINTS = {
    cacheKey: 'godot-instanced-points',
    onBeforeCompile(shader) {
        shader.vertexShader = `#define USE_INSTANCING
attribute mat4 ${INSTANCE_MATRIX_ATTRIBUTE};
${shader.vertexShader}`;
    },
};
/** The uniforms of a points colour pass, each with the value it holds until the material's userData records one. */
const POINT_SIZE_UNIFORMS = {
    [POINT_SIZE_KEY]: BASE_MATERIAL_3D_DEFAULTS.pointSize,
    [POINT_PIXEL_RATIO_KEY]: 1,
};
/** Writes `gl_PointSize` as the product of `uniforms`, each recorded from the material's userData. */
function pointSizeInjection(cacheKey, uniforms) {
    const keys = Object.keys(uniforms);
    const declarations = keys.map((key) => `uniform float ${key};`).join('\n');
    return {
        cacheKey,
        onBeforeCompile(shader) {
            for (const [key, initial] of Object.entries(uniforms)) {
                shader.uniforms[key] = recordedUniform(this, key, initial);
            }
            const size = `${PROJECT_CHUNK}\n\tgl_PointSize = ${keys.join(' * ')};`;
            editedProgram(shader, { vertex: [{ three: PROJECT_CHUNK, godot: size }] }, 'point size');
            shader.vertexShader = `${declarations}\n${shader.vertexShader}`;
        },
    };
}
/** What a material compiles with to draw a points surface, at its size in canvas pixels. */
const GODOT_POINT_SIZE = pointSizeInjection('godot-point-size', POINT_SIZE_UNIFORMS);
/** What a depth draw compiles with to rasterise a point at its size in the shadow map's texels. */
const GODOT_POINT_SHADOW_SIZE = pointSizeInjection('godot-point-shadow-size', {
    [POINT_SIZE_KEY]: POINT_SIZE_UNIFORMS[POINT_SIZE_KEY],
});
const MAP_PARS_CHUNK = '#include <map_pars_fragment>';
/**
 * The map's UV as the point coordinate. A macro after the varying's declaration, so every read of
 * the map, whichever patch wrote it, takes the coordinate. Point coordinates run down from the top
 * as an image does, and three's V runs up, as in three's own `map_particle_fragment`.
 */
const GODOT_POINT_COORD_ALBEDO = {
    cacheKey: 'godot-point-coord-albedo',
    onBeforeCompile(shader) {
        editedProgram(shader, {
            fragment: [
                {
                    three: MAP_PARS_CHUNK,
                    godot: `${MAP_PARS_CHUNK}\n#define vMapUv vec2( gl_PointCoord.x, 1.0 - gl_PointCoord.y )`,
                },
            ],
        }, 'point coordinate albedo');
    },
};
/** Each bag's point forms, filled only by `withPointDraw`. */
const pointForms = bagForms();
/**
 * `bag` as a material for a points surface, or `bag` itself for no draw. The cull mode does not
 * reach a point, which is never a back face, so the side is the front.
 */
export function withPointDraw(bag, draw) {
    if (!draw)
        return bag;
    const drawKey = `${draw.pixels}:${draw.pixelRatio}:${draw.samplesAlbedoAtPointCoord}:${draw.placement}`;
    return pointForms(bag, drawKey, () => pointBag(bag, draw));
}
function pointBag(bag, draw) {
    const readsMap = draw.samplesAlbedoAtPointCoord && Boolean(bag.props.map);
    const isAtInstances = draw.placement === 'instances';
    const userData = withDepthInjection({
        ...bag.props.userData,
        [POINT_SIZE_KEY]: draw.pixels,
        [POINT_PIXEL_RATIO_KEY]: draw.pixelRatio,
        [POINT_COORD_ALBEDO_KEY]: readsMap,
        [POINTS_AT_INSTANCES_KEY]: isAtInstances,
    }, 'beforeCut', pointShadowSizeInjection(readsMap, isAtInstances));
    return {
        ...bag,
        props: { ...bag.props, side: FrontSide, userData },
        injection: composeInjections([
            bag.injection,
            GODOT_POINT_SIZE,
            readsMap ? GODOT_POINT_COORD_ALBEDO : undefined,
            isAtInstances ? GODOT_INSTANCED_POINTS : undefined,
        ]),
    };
}
const GODOT_POINT_SHADOW_SIZE_AT_POINT_COORD = composeInjections([
    GODOT_POINT_SHADOW_SIZE,
    GODOT_POINT_COORD_ALBEDO,
]);
/**
 * What a depth draw compiles with to cut a points surface's shadow as its colour pass draws the
 * points: at their size, across the square where the albedo map is read, and at each instance.
 */
function pointShadowSizeInjection(readsMap, isAtInstances) {
    const size = readsMap ? GODOT_POINT_SHADOW_SIZE_AT_POINT_COORD : GODOT_POINT_SHADOW_SIZE;
    return isAtInstances ? composeInjections([size, GODOT_INSTANCED_POINTS]) : size;
}
//# sourceMappingURL=godotPoint.js.map